import React, { useState, useEffect, useRef } from 'react';
import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import { deleteEquipmentItem, updateEquipmentItem } from '../../graphql/mutations';
import { DataStore } from '../../utils/GraphQLDataStoreCompat';
import DataStoreUtil from '../../utils/DataStoreSync';
import { getConnectivityModeSnapshot, subscribeConnectivityMode } from '../../offline/connectivityMode';
import {
  getCachedActiveUIC,
  getUnitOfflineSnapshot,
  loadCachedUnitOfflineSnapshot,
  setCachedActiveUIC
} from '../../offline/offlineCustodyRepository';
import AssignmentModal from '../assignment/AssignmentModal';
import './Equipment.css';
import './InventoryStyles.css';

function InventoryList({ onBack }) {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('lin');
  const [sortDirection, setSortDirection] = useState('asc');
  const [uicID, setUicID] = useState(null);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [selectedItemsToDelete, setSelectedItemsToDelete] = useState({});
  const [isOnlineMode, setIsOnlineMode] = useState(getConnectivityModeSnapshot() !== 'offline');
  const [showOrphanedItemsModal, setShowOrphanedItemsModal] = useState(false);
  const [orphanedItems, setOrphanedItems] = useState([]);
  const [processingOrphans, setProcessingOrphans] = useState(false);

  // Assignment Modal state
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [itemToAssign, setItemToAssign] = useState(null);

  const [openActionMenuId, setOpenActionMenuId] = useState(null); // State to track open menu

  // Ref for detecting clicks outside the action menu
  const actionMenuRef = useRef(null);

  const client = generateClient();

  const fetchCurrentUserProfile = async () => {
    const { username } = await getCurrentUser();
    const userResponse = await client.graphql({
      query: `query GetUserByOwner($owner: String!) {
        usersByOwner(owner: $owner, limit: 1) {
          items {
            id
            uicID
          }
        }
      }`,
      variables: { owner: username },
      authMode: 'userPool',
      headers: {
        'Cache-Control': 'no-cache',
        'x-cache-buster': new Date().getTime().toString()
      }
    });

    return userResponse.data.usersByOwner.items[0] || null;
  };

  const resolveActiveUICID = async ({ allowNetwork = true } = {}) => {
    if (uicID) {
      return uicID;
    }

    if (allowNetwork) {
      try {
        const userData = await fetchCurrentUserProfile();
        if (userData?.uicID) {
          setUicID(userData.uicID);
          await setCachedActiveUIC(userData.uicID);
          return userData.uicID;
        }
      } catch (error) {
        console.warn('Could not resolve UIC from backend profile, falling back to offline cache:', error);
      }
    }

    const cachedUIC = await getCachedActiveUIC();
    if (cachedUIC) {
      setUicID(cachedUIC);
      return cachedUIC;
    }

    return null;
  };

  const snapshotToInventoryItems = (snapshot) => {
    if (snapshot?.custodyRows?.length) {
      return snapshot.custodyRows.map(row => ({
        id: row.equipmentItemID,
        uicID: snapshot.uicID,
        equipmentMasterID: row.equipmentMasterID,
        nsn: row.nsn || '',
        lin: row.lin || '',
        serialNumber: row.serialNumber || '',
        stockNumber: row.stockNumber || '',
        location: row.location || '',
        assignedToID: row.assignedToID || null,
        assignedToName: row.assignedToName,
        maintenanceStatus: row.maintenanceStatus || 'OPERATIONAL',
        isPartOfGroup: Boolean(row.groupID),
        groupID: row.groupID || null,
        updatedAt: row.itemUpdatedAt,
        _version: row.itemVersion,
        commonName: row.commonName || `Item ${row.nsn || ''}`,
        isSerialTracked: row.isSerialTracked,
        isSensitiveItem: row.isSensitiveItem,
        isConsumable: false
      }));
    }

    const soldiersById = new Map((snapshot?.soldiers || []).map(soldier => [soldier.id, soldier]));
    const mastersById = new Map((snapshot?.equipmentMasters || []).map(master => [master.id, master]));

    return (snapshot?.equipmentItems || []).map(item => {
      const master = item.equipmentMasterID ? mastersById.get(item.equipmentMasterID) : null;
      const soldier = item.assignedToID ? soldiersById.get(item.assignedToID) : null;

      return {
        ...item,
        commonName: master?.commonName || `Item ${item.nsn || ''}`,
        isSerialTracked: Boolean(master?.isSerialTracked),
        isSensitiveItem: Boolean(master?.isSensitiveItem),
        isConsumable: Boolean(master?.isConsumable),
        assignedToName: soldier ? `${soldier.rank || ''} ${soldier.lastName}, ${soldier.firstName}`.trim() : null
      };
    });
  };

  // Effect to clear messages after a timeout
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        if (success) setSuccess('');
        if (error) setError('');
      }, 5000); // Clear after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Check for connectivity mode changes
  useEffect(() => {
    const checkConnectivityMode = (mode = getConnectivityModeSnapshot()) => {
      setIsOnlineMode(mode !== 'offline');
    };

    checkConnectivityMode();
    return subscribeConnectivityMode(checkConnectivityMode);
  }, []);

  // Load inventory based on connectivity mode
  useEffect(() => {
    if (isOnlineMode) {
      fetchDirectFromAPI();
    } else {
      fetchFromLocalDataStore();
    }
    // Legacy inventory loading is function-scoped; keep this effect keyed to mode changes only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnlineMode]);

  // Fetch data from the durable offline custody snapshot.
  const fetchFromLocalDataStore = async () => {
    try {
      setLoading(true);
      setInventory([]); // Clear existing inventory

      const activeUICID = await resolveActiveUICID({ allowNetwork: navigator.onLine });
      if (!activeUICID) {
        setError('You must be assigned to a UIC to view inventory.');
        setLoading(false);
        return;
      }

      let snapshot = await loadCachedUnitOfflineSnapshot(activeUICID);
      let processedItems = snapshotToInventoryItems(snapshot);
      if (processedItems.length === 0) {
        if (navigator.onLine) {
          snapshot = await getUnitOfflineSnapshot(activeUICID);
          processedItems = snapshotToInventoryItems(snapshot);
        } else {
          setError('No offline inventory snapshot is available for this UIC. Connect once and sync data before going offline.');
          return;
        }
      }

      setInventory(processedItems);
    } catch (error) {
      console.error('Error fetching offline custody snapshot:', error);
      setError(`Failed to load inventory from local storage: ${error.message}. Try enabling online mode.`);
    } finally {
      setLoading(false);
    }
  };

  // Function to clear DataStore and force a fresh sync
  const handleSyncData = async () => {
    // Only allow sync in online mode
    if (!isOnlineMode) {
      alert('Cannot sync in offline mode. Please switch to connected mode first.');
      return;
    }

    try {
      setSyncInProgress(true);
      setLoading(true);

      await DataStoreUtil.clearAndSync();
      const activeUICID = await resolveActiveUICID({ allowNetwork: true });
      if (!activeUICID) {
        setError('You must be assigned to a UIC before inventory can be cached for offline use.');
        return;
      }

      const snapshot = await getUnitOfflineSnapshot(activeUICID);
      const cachedItemCount = snapshotToInventoryItems(snapshot).length;
      if (cachedItemCount === 0) {
        setError('Sync completed, but no inventory rows were cached for offline use.');
      } else {
        setSuccess(`Synced ${cachedItemCount} inventory items for offline use.`);
      }

      // Use the direct API fetch method for consistency
      await fetchDirectFromAPI();
    } catch (error) {
      console.error('Error syncing data:', error);
      setError(`Failed to sync data: ${error.message || 'Please try again.'}`);
    } finally {
      setSyncInProgress(false);
      setLoading(false);
    }
  };

  // Function to force a complete refresh of the page while preserving navigation
  const handleHardRefresh = () => {
    if (window.confirm('This will reload the entire page. Continue?')) {
      // Store the current URL to preserve navigation
      const currentPath = window.location.pathname;

      // Use sessionStorage to store that we want to stay on the /manage page
      // after the refresh
      sessionStorage.setItem('returnToPath', currentPath);
      sessionStorage.setItem('openInventoryView', 'true');

      // Then reload the page
      window.location.reload(true);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const sortedInventory = () => {
    if (!inventory) return [];

    const filtered = inventory.filter(item => {
      const searchLower = searchTerm.toLowerCase();
      return (
        item.lin?.toLowerCase().includes(searchLower) ||
        item.nsn?.toLowerCase().includes(searchLower) ||
        item.serialNumber?.toLowerCase().includes(searchLower) ||
        item.commonName?.toLowerCase().includes(searchLower) ||
        item.location?.toLowerCase().includes(searchLower)
      );
    });

    return filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Convert to string for comparison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();

      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  };

  const handleEditItem = (item) => {
    setEditItem({ ...item });
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);

      // Explicitly pick only the fields that belong to EquipmentItem and are editable here
      const input = {
        id: editItem.id,
        _version: editItem._version, // Important for conflict detection
        lin: editItem.lin,
        serialNumber: editItem.serialNumber || null, // Use null for empty optional strings
        stockNumber: editItem.stockNumber || null,   // Use null for empty optional strings
        location: editItem.location || null,       // Use null for empty optional strings
        maintenanceStatus: editItem.maintenanceStatus || 'OPERATIONAL' // Ensure a valid status
        // assignedToID is handled by handleAssignToUser, do not include here unless the edit modal also changes it
      };

      // Remove fields that are null or undefined IF they are optional in the schema
      // (serialNumber, stockNumber, location, maintenanceStatus are optional)
      if (input.serialNumber === null) delete input.serialNumber;
      if (input.stockNumber === null) delete input.stockNumber;
      if (input.location === null) delete input.location;
      // maintenanceStatus has a default, so it should always be sent

      console.log("Updating item with input:", input);

      const updatedItemResult = await client.graphql({
        query: updateEquipmentItem,
        variables: {
          input
        }
      });

      const updatedItemData = updatedItemResult.data.updateEquipmentItem;

      // Update local state - merge with existing item to preserve frontend-only fields
      setInventory(inventory.map(item =>
        item.id === input.id
          ? { ...item, ...updatedItemData }
          : item
      ));

      // Also update in local DataStore if we're in online mode
      if (isOnlineMode) {
        try {
          const { EquipmentItem } = await import('../../models');
          const localItem = await DataStore.query(EquipmentItem, input.id);
          if (localItem) {
            await DataStore.save(
              EquipmentItem.copyOf(localItem, updated => {
                // Update only the fields that were actually changed
                updated.lin = input.lin;
                updated.serialNumber = input.serialNumber;
                updated.stockNumber = input.stockNumber;
                updated.location = input.location;
                updated.maintenanceStatus = input.maintenanceStatus;
                // Do not update assignedToID here
              })
            );
            console.log("Updated local DataStore item:", input.id);
          }
        } catch (datastoreError) {
          console.log('DataStore sync error (non-critical) during update:', datastoreError);
        }
      }

      setEditItem(null);
      setSuccess('Item updated successfully.');
    } catch (error) {
      console.error('Error updating item:', error);
      setError('Failed to update item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      setLoading(true);

      // Try direct deletion using a custom mutation that ignores version conflicts
      await client.graphql({
        query: `mutation ForceDeleteEquipmentItem($id: ID!) {
          deleteEquipmentItem(input: { id: $id }, condition: null) {
            id
          }
        }`,
        variables: { id }
      });

      // Fetch fresh data directly after deletion
      await fetchDirectFromAPI();

      console.log('Successfully deleted item with force delete approach');
    } catch (error) {
      console.error('Error deleting item:', error);

      // If the direct mutation fails, try with the normal approach as a fallback
      try {
        await client.graphql({
          query: deleteEquipmentItem,
          variables: {
            input: { id }
          }
        });

        // Fetch fresh data directly after deletion
        await fetchDirectFromAPI();

        console.log('Successfully deleted item with standard approach');
      } catch (fallbackError) {
        console.error('Fallback delete also failed:', fallbackError);
        setError('Failed to delete item. Please try again or ask an admin to review sync state.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Find duplicate items based on NSN + Serial Number
  const findDuplicates = () => {
    if (!inventory || inventory.length === 0) {
      alert('No inventory items to check for duplicates.');
      return;
    }

    // Group by NSN + Serial Number
    const groups = {};
    inventory.forEach(item => {
      if (item.serialNumber) {
        const key = `${item.nsn}|${item.serialNumber}`;
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(item);
      }
    });

    // Filter for groups with more than one item (duplicates)
    const duplicates = Object.entries(groups)
      .filter(([key, items]) => items.length > 1)
      .map(([key, items]) => ({
        key,
        nsn: items[0].nsn,
        serialNumber: items[0].serialNumber,
        lin: items[0].lin,
        items: items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // newest first
      }));

    if (duplicates.length === 0) {
      alert('No duplicates found in your inventory.');
      return;
    }

    // Initialize selection state - select all items except the newest (first) one
    const initialSelection = {};
    duplicates.forEach(group => {
      // Skip the first item (newest) and mark the rest for deletion
      group.items.slice(1).forEach(item => {
        initialSelection[item.id] = true;
      });
    });

    setSelectedItemsToDelete(initialSelection);
    setDuplicateGroups(duplicates);
    setShowDuplicatesModal(true);
  };

  // Toggle selection of an item for deletion
  const toggleItemSelection = (itemId) => {
    setSelectedItemsToDelete(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Delete selected duplicate items
  const deleteDuplicates = async () => {
    const itemsToDelete = Object.entries(selectedItemsToDelete)
      .filter(([id, isSelected]) => isSelected)
      .map(([id]) => id);

    if (itemsToDelete.length === 0) {
      alert('No items selected for deletion.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${itemsToDelete.length} duplicate items?`)) {
      return;
    }

    try {
      setLoading(true);

      // Delete items one by one
      const results = {
        success: 0,
        failed: 0
      };

      for (const id of itemsToDelete) {
        try {
          // Try direct deletion using a custom mutation that ignores version conflicts
          await client.graphql({
            query: `mutation ForceDeleteEquipmentItem($id: ID!) {
              deleteEquipmentItem(input: { id: $id }, condition: null) {
                id
              }
            }`,
            variables: { id }
          });

          console.log('Successfully deleted item with force delete approach:', id);
          results.success++;
        } catch (error) {
          console.error(`Error force deleting item ${id}:`, error);

          // Try the standard approach as fallback
          try {
            await client.graphql({
              query: deleteEquipmentItem,
              variables: {
                input: { id }
              }
            });

            console.log('Successfully deleted item with standard approach:', id);
            results.success++;
          } catch (fallbackError) {
            console.error(`Both delete approaches failed for item ${id}:`, fallbackError);
            results.failed++;
          }
        }
      }

      // Close modal and fetch fresh data directly
      setShowDuplicatesModal(false);
      await fetchDirectFromAPI();

      if (results.failed > 0) {
        alert(`Successfully deleted ${results.success} items. Failed to delete ${results.failed} items.`);
      } else {
        alert(`Successfully deleted all ${results.success} selected items.`);
      }
    } catch (error) {
      console.error('Error in duplicate deletion process:', error);
      setError('Failed to delete duplicate items. Please try again or ask an admin to review sync state.');
    } finally {
      setLoading(false);
    }
  };

  // Add a button to the component to refresh using the direct fetch method
  const handleForceRefresh = async () => {
    // Use the appropriate fetch method based on connectivity mode
    try {
      setLoading(true);
      if (isOnlineMode) {
        await fetchDirectFromAPI();
      } else {
        await fetchFromLocalDataStore();
      }
    } catch (error) {
      console.error('Error in force refresh:', error);
      setError('Failed to refresh inventory data.');
    } finally {
      setLoading(false);
    }
  };

  // Direct fetch method that matches the DataInspector approach
  const fetchDirectFromAPI = async () => {
    try {
      setLoading(true);
      setInventory([]); // Clear existing inventory

      // Get user's UIC
      const userData = await fetchCurrentUserProfile();
      if (!userData || !userData.uicID) {
        setError('You must be assigned to a UIC to view inventory.');
        setLoading(false);
        return;
      }

      setUicID(userData.uicID);
      await setCachedActiveUIC(userData.uicID);

      // Fetch equipment items directly, matching the debug tool approach
      const equipmentResponse = await client.graphql({
        query: `query EquipmentItemsByUicID($uicID: ID!) {
          equipmentItemsByUicID(
            uicID: $uicID,
            filter: { _deleted: { ne: true } }
          ) {
            items {
              id
              uicID
              equipmentMasterID
              nsn
              lin
              serialNumber
              stockNumber
              location
              assignedToID
              isPartOfGroup
              groupID
              maintenanceStatus
              createdAt
              updatedAt
              _version
              _deleted
              _lastChangedAt
            }
          }
        }`,
        variables: { uicID: userData.uicID },
        headers: {
          'Cache-Control': 'no-cache',
          'x-cache-buster': new Date().getTime().toString()
        }
      });

      const items = equipmentResponse.data.equipmentItemsByUicID.items;
      console.log('Direct API fetch returned:', items.length, 'items');

      // Get all soldier data for the UIC to handle assignments
      let soldiersMap = {};
      try {
        const soldiersResponse = await client.graphql({
          query: `query GetSoldiersByUIC($uicID: ID!) {
            soldiersByUicID(uicID: $uicID) {
              items {
                id
                firstName
                lastName
                rank
              }
            }
          }`,
          variables: { uicID: userData.uicID },
          headers: {
            'Cache-Control': 'no-cache',
            'x-cache-buster': new Date().getTime().toString()
          }
        });

        const soldiersList = soldiersResponse.data.soldiersByUicID.items;
        soldiersList.forEach(soldier => {
          soldiersMap[soldier.id] = soldier;
        });
      } catch (error) {
        console.error('Error fetching soldiers:', error);
      }

      // Process these items to add master data and soldier information
      const itemsWithDetails = await Promise.all(items.map(async (item) => {
        try {
          const masterResponse = await client.graphql({
            query: `query GetEquipmentMaster($id: ID!) {
              getEquipmentMaster(id: $id) {
                id
                nsn
                commonName
                isSerialTracked
                isSensitiveItem
                isConsumable
              }
            }`,
            variables: { id: item.equipmentMasterID },
            headers: {
              'Cache-Control': 'no-cache',
              'x-cache-buster': new Date().getTime().toString()
            }
          });

          const masterInfo = masterResponse.data.getEquipmentMaster;

          // Add soldier information if assigned
          let assignedToName = null;
          if (item.assignedToID && soldiersMap[item.assignedToID]) {
            const soldier = soldiersMap[item.assignedToID];
            assignedToName = `${soldier.rank || ''} ${soldier.lastName}, ${soldier.firstName}`;
          }

          return {
            ...item,
            commonName: masterInfo?.commonName || `Item ${item.nsn}`,
            isSerialTracked: masterInfo?.isSerialTracked || false,
            isSensitiveItem: masterInfo?.isSensitiveItem || false,
            isConsumable: masterInfo?.isConsumable || false,
            assignedToName: assignedToName
          };
        } catch (error) {
          console.error('Error fetching master data for item:', item.id, error);
          return {
            ...item,
            commonName: `Item ${item.nsn}`,
            isSerialTracked: false,
            isSensitiveItem: false,
            isConsumable: false
          };
        }
      }));

      console.log('Setting inventory with direct-fetched data:', itemsWithDetails.length, 'items');
      setInventory(itemsWithDetails);
    } catch (error) {
      console.error('Error in direct API fetch:', error);
      setError('Failed to load inventory directly from API.');
    } finally {
      setLoading(false);
    }
  };

  // After fetchDirectFromAPI, add this new function to find orphaned group items
  const findOrphanedGroupItems = async () => {
    try {
      setLoading(true);

      if (!uicID) {
        // Get user's UIC if we don't have it
        const { username } = await getCurrentUser();
        const userResponse = await client.graphql({
          query: `query GetUserByOwner($owner: String!) {
            usersByOwner(owner: $owner, limit: 1) {
              items {
                id
                uicID
              }
            }
          }`,
          variables: { owner: username }
        });

        const userData = userResponse.data.usersByOwner.items[0];
        if (!userData || !userData.uicID) {
          setError('You must be assigned to a UIC to perform this operation.');
          setLoading(false);
          return;
        }

        setUicID(userData.uicID);
      }

      // Fetch all equipment items that have a groupID
      const response = await client.graphql({
        query: `query GetItemsWithGroups($uicID: ID!) {
          equipmentItemsByUicID(
            uicID: $uicID,
            filter: {
              isPartOfGroup: { eq: true },
              _deleted: { ne: true }
            }
          ) {
            items {
              id
              nsn
              lin
              serialNumber
              stockNumber
              equipmentMasterID
              isPartOfGroup
              groupID
              _version
            }
          }
        }`,
        variables: { uicID }
      });

      const groupedItems = response.data.equipmentItemsByUicID.items;

      if (groupedItems.length === 0) {
        alert('No items found that are part of a group.');
        setLoading(false);
        return;
      }

      // Get a list of all valid groups
      const groupsResponse = await client.graphql({
        query: `query GetGroups($uicID: ID!) {
          equipmentGroupsByUicID(
            uicID: $uicID,
            filter: { _deleted: { ne: true } }
          ) {
            items {
              id
            }
          }
        }`,
        variables: { uicID }
      });

      const validGroupIds = groupsResponse.data.equipmentGroupsByUicID.items.map(g => g.id);

      // Find items that reference deleted groups
      const orphaned = groupedItems.filter(item =>
        !validGroupIds.includes(item.groupID)
      );

      if (orphaned.length === 0) {
        alert('No orphaned items found. All grouped items reference valid groups.');
        setLoading(false);
        return;
      }

      // Add item details
      const orphanedWithDetails = await Promise.all(orphaned.map(async (item) => {
        try {
          const masterResponse = await client.graphql({
            query: `query GetEquipmentMaster($id: ID!) {
              getEquipmentMaster(id: $id) {
                id
                commonName
              }
            }`,
            variables: { id: item.equipmentMasterID }
          });

          const masterInfo = masterResponse.data.getEquipmentMaster;
          return {
            ...item,
            commonName: masterInfo?.commonName || `Item ${item.nsn}`
          };
        } catch (error) {
          return {
            ...item,
            commonName: `Item ${item.nsn}`
          };
        }
      }));

      setOrphanedItems(orphanedWithDetails);
      setShowOrphanedItemsModal(true);
    } catch (error) {
      console.error('Error finding orphaned items:', error);
      setError('Failed to find orphaned items. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Function to release orphaned items
  const handleReleaseOrphanedItems = async () => {
    if (orphanedItems.length === 0) {
      setShowOrphanedItemsModal(false);
      return;
    }

    if (!window.confirm(`Are you sure you want to release ${orphanedItems.length} orphaned items from deleted groups?`)) {
      return;
    }

    try {
      setProcessingOrphans(true);

      let successCount = 0;
      let failCount = 0;

      // Process each orphaned item
      for (const item of orphanedItems) {
        try {
          console.log(`Attempting to release item ${item.id}...`);

          // First attempt: Use direct mutation with condition: null
          try {
            await client.graphql({
              query: `mutation ReleaseOrphanedItem($id: ID!) {
                updateEquipmentItem(
                  input: {
                    id: $id,
                    isPartOfGroup: false,
                    groupID: null
                  },
                  condition: null
                ) {
                  id
                  isPartOfGroup
                  groupID
                }
              }`,
              variables: { id: item.id }
            });

            console.log(`Successfully released item ${item.id} using direct approach`);
            successCount++;
            continue; // Skip to next item
          } catch (directError) {
            console.error(`Direct approach failed for item ${item.id}:`, directError);
            // Continue to fallback approaches
          }

          // Second attempt: Get the latest version and update with that version
          try {
            // Get the current version of the item
            const getItemResponse = await client.graphql({
              query: `query GetEquipmentItem($id: ID!) {
                getEquipmentItem(id: $id) {
                  id
                  _version
                  isPartOfGroup
                  groupID
                }
              }`,
              variables: { id: item.id }
            });

            const currentItem = getItemResponse.data.getEquipmentItem;
            if (!currentItem) {
              throw new Error('Item not found');
            }

            // Check if the item is already released
            if (!currentItem.isPartOfGroup && !currentItem.groupID) {
              console.log(`Item ${item.id} is already released, skipping...`);
              successCount++;
              continue; // Skip to next item
            }

            // Update with the latest version
            await client.graphql({
              query: updateEquipmentItem,
              variables: {
                input: {
                  id: item.id,
                  isPartOfGroup: false,
                  groupID: null,
                  _version: currentItem._version
                }
              }
            });

            console.log(`Successfully released item ${item.id} using versioned approach`);
            successCount++;
          } catch (versionedError) {
            console.error(`Versioned approach failed for item ${item.id}:`, versionedError);

            // Third attempt: Super aggressive approach - use raw DynamoDB update
            try {
              const rawResult = await client.graphql({
                query: `mutation ForceReleaseItem($id: ID!) {
                  updateEquipmentItem(
                    input: {
                      id: $id,
                      isPartOfGroup: false,
                      groupID: null
                    },
                    condition: {
                      id: {
                        eq: $id
                      }
                    }
                  ) {
                    id
                  }
                }`,
                variables: { id: item.id }
              });

              console.log(`Successfully released item ${item.id} using last-resort approach`, rawResult);
              successCount++;
            } catch (finalError) {
              console.error(`All approaches failed for item ${item.id}:`, finalError);
              failCount++;
            }
          }
        } catch (error) {
          console.error(`Error processing item ${item.id}:`, error);
          failCount++;
        }
      }

      // Sync changes to local DataStore if in online mode
      if (isOnlineMode) {
        try {
          await DataStoreUtil.clearAndSync();
        } catch (syncError) {
          console.error('Error syncing DataStore after releasing orphaned items:', syncError);
        }
      }

      // Refresh data
      await fetchDirectFromAPI();

      // Show result with detailed information
      setShowOrphanedItemsModal(false);

      if (failCount > 0) {
        setError(`Released ${successCount} orphaned items, but failed to release ${failCount} items. Try again for the remaining items.`);
      } else {
        setSuccess(`Successfully released all ${successCount} orphaned items. These items can now be added to new groups.`);
      }
    } catch (error) {
      console.error('Error releasing orphaned items:', error);
      setError('Failed to release orphaned items. Please try again.');
    } finally {
      setProcessingOrphans(false);
    }
  };

  // Add this new function to handle opening the AssignmentModal with preselected item
  const handleOpenAssignmentModal = async (item) => {
    try {
      setLoading(true);

      // If the item is part of a group, we need to preselect the group
      if (item.isPartOfGroup && item.groupID) {
        // Get the group details
        const groupResponse = await client.graphql({
          query: `query GetEquipmentGroup($id: ID!) {
            getEquipmentGroup(id: $id) {
              id
              name
              description
              assignedToID
              _version
            }
          }`,
          variables: { id: item.groupID }
        });

        const groupData = groupResponse.data.getEquipmentGroup;

        if (groupData) {
          // Set the item to assign with group information
          setItemToAssign({
            ...item,
            group: groupData
          });
        } else {
          // Group not found (might be deleted), just use the item
          setItemToAssign(item);
        }
      } else {
        // Not part of a group, just use the item
        setItemToAssign(item);
      }

      // Enable the modal after data is loaded
      setShowAssignmentModal(true);
    } catch (error) {
      console.error('Error preparing for assignment:', error);
      setError('Failed to prepare item for assignment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle completing assignment
  const handleAssignmentComplete = async () => {
    // Refresh the inventory list to show updated assignments
    try {
      if (isOnlineMode) {
        await fetchDirectFromAPI();
      } else {
        await fetchFromLocalDataStore();
      }
      setSuccess('Assignment updated successfully.');
    } catch (error) {
      console.error('Error refreshing inventory after assignment:', error);
    }
  };

  // Toggle action menu for a specific item
  const toggleActionMenu = (itemId) => {
    setOpenActionMenuId(prevId => (prevId === itemId ? null : itemId));
  };

  return (
    <div className="inventory-container">
      <div className="inventory-header">
        <h2>Unit Inventory</h2>
        <div className="header-actions">
          <button
            className="action-button"
            onClick={handleSyncData}
            disabled={syncInProgress || loading || !isOnlineMode}
            title={isOnlineMode ? 'Sync with backend' : 'Switch to connected mode to sync'}
          >
            {syncInProgress ? 'Syncing...' : 'Sync Data'}
          </button>
          <button
            className="action-button"
            onClick={findDuplicates}
            disabled={loading || syncInProgress}
          >
            Find Duplicates
          </button>
          <button
            className="action-button orange-button"
            onClick={findOrphanedGroupItems}
            disabled={loading || syncInProgress}
          >
            Fix Orphaned Items
          </button>
          <button
            className="action-button"
            onClick={handleHardRefresh}
            disabled={loading || syncInProgress}
          >
            Hard Refresh
          </button>
          <button
            className="action-button direct-fetch-button"
            onClick={handleForceRefresh}
            disabled={loading}
          >
            {isOnlineMode ? 'Direct Fetch' : 'Refresh Local'}
          </button>
          <button className="back-button" onClick={onBack}>
            &larr; Back
          </button>
        </div>
      </div>

      {/* Mode indicator */}
      <div className={`mode-indicator ${isOnlineMode ? 'online-mode' : 'offline-mode'}`}>
        {isOnlineMode
          ? 'Connected Mode: Syncing with backend'
          : 'Offline Mode: Using local data (toggle in header to sync)'
        }
      </div>

      <div className="search-container">
        <input
          type="text"
          placeholder="Search inventory..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {loading && <div className="loading">Loading inventory...</div>}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      {!loading && inventory.length === 0 && (
        <div className="no-data">No equipment items found. Add some using the "Add New Equipment" button.</div>
      )}

      {!loading && inventory.length > 0 && (
        <div className="table-container">
          <table className="inventory-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('lin')}>
                  LIN {sortBy === 'lin' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('nsn')}>
                  NSN {sortBy === 'nsn' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('commonName')}>
                  Name {sortBy === 'commonName' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('serialNumber')}>
                  Serial # {sortBy === 'serialNumber' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('stockNumber')}>
                  Stock # {sortBy === 'stockNumber' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('location')}>
                  Location {sortBy === 'location' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('assignedToID')}>
                  Assigned To {sortBy === 'assignedToID' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('maintenanceStatus')}>
                  Status {sortBy === 'maintenanceStatus' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedInventory().map(item => (
                <tr key={item.id} className={item.isPartOfGroup ? 'grouped-item' : ''}>
                  <td>{item.lin}</td>
                  <td>{item.nsn}</td>
                  <td>
                    {item.commonName}
                    {item.isPartOfGroup && (
                      <span className="group-badge" title="This item is part of a group">Group</span>
                    )}
                  </td>
                  <td>{item.serialNumber || '-'}</td>
                  <td>{item.stockNumber || '-'}</td>
                  <td>{item.location || '-'}</td>
                  <td>{item.assignedToName || (item.assignedToID ? `ID: ${item.assignedToID}` : '-')}</td>
                  <td>{item.maintenanceStatus || 'OPERATIONAL'}</td>
                  <td className="action-cell">
                    <div className="action-button-container">
                      <button
                        className="action-menu-button"
                        onClick={() => toggleActionMenu(item.id)}
                      >
                        ...
                      </button>
                      {openActionMenuId === item.id && (
                        <div className="action-menu" ref={actionMenuRef}>
                          <button
                            className="action-menu-item assign-item"
                            onClick={() => {
                              handleOpenAssignmentModal(item);
                              setOpenActionMenuId(null); // Close menu after click
                            }}
                          >
                            Assign
                          </button>
                          <button
                            className="action-menu-item delete-item"
                            onClick={() => {
                              handleDeleteItem(item.id);
                              setOpenActionMenuId(null); // Close menu after click
                            }}
                          >
                            Delete
                          </button>
                          <button
                            className="action-menu-item edit-item"
                            onClick={() => {
                              handleEditItem(item);
                              setOpenActionMenuId(null); // Close menu after click
                            }}
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Duplicates Modal */}
      {showDuplicatesModal && (
        <div className="modal-overlay">
          <div className="modal-content duplicates-modal">
            <h2>Duplicate Items</h2>
            <p>Found {duplicateGroups.length} groups of duplicate items with the same NSN and Serial Number.</p>
            <p>The newest item in each group is recommended to keep (unchecked by default).</p>

            <div className="duplicates-list">
              {duplicateGroups.map((group, groupIndex) => (
                <div key={group.key} className="duplicate-group">
                  <h3>Group {groupIndex + 1}: {group.lin} - {group.nsn}</h3>
                  <p>Serial Number: {group.serialNumber}</p>

                  <table className="duplicates-table">
                    <thead>
                      <tr>
                        <th>Delete</th>
                        <th>Created</th>
                        <th>ID</th>
                        <th>Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((item, itemIndex) => (
                        <tr key={item.id} className={itemIndex === 0 ? 'newest-item' : ''}>
                          <td>
                            <input
                              type="checkbox"
                              checked={!!selectedItemsToDelete[item.id]}
                              onChange={() => toggleItemSelection(item.id)}
                            />
                          </td>
                          <td>{new Date(item.createdAt).toLocaleString()}</td>
                          <td>{item.id.substring(0, 8)}...</td>
                          <td>{item.location || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>

            <div className="modal-actions">
              <button
                className="secondary-button"
                onClick={() => setShowDuplicatesModal(false)}
              >
                Cancel
              </button>
              <button
                className="primary-button"
                onClick={deleteDuplicates}
                disabled={Object.values(selectedItemsToDelete).filter(Boolean).length === 0}
              >
                Delete Selected Items
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Orphaned Items Modal */}
      {showOrphanedItemsModal && (
        <div className="modal-overlay">
          <div className="modal-content orphaned-modal">
            <h2>Orphaned Items</h2>
            <p>Found {orphanedItems.length} items that are marked as part of a group, but the group no longer exists.</p>
            <p>These items need to be released from their deleted groups before they can be added to new groups.</p>

            <div className="orphaned-items-list">
              <table className="orphaned-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>NSN</th>
                    <th>Serial #</th>
                    <th>Stock #</th>
                    <th>Deleted Group ID</th>
                  </tr>
                </thead>
                <tbody>
                  {orphanedItems.map(item => (
                    <tr key={item.id}>
                      <td>{item.commonName}</td>
                      <td>{item.nsn}</td>
                      <td>{item.serialNumber || '-'}</td>
                      <td>{item.stockNumber || '-'}</td>
                      <td className="group-id">{item.groupID}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="modal-actions">
              <button
                className="secondary-button"
                onClick={() => setShowOrphanedItemsModal(false)}
                disabled={processingOrphans}
              >
                Cancel
              </button>
              <button
                className="primary-button"
                onClick={handleReleaseOrphanedItems}
                disabled={processingOrphans}
              >
                {processingOrphans ? 'Releasing...' : 'Release All Items'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && itemToAssign && (
        <AssignmentModal
          onClose={() => {
            setShowAssignmentModal(false);
            setItemToAssign(null);
          }}
          uicID={uicID}
          preselectedItem={itemToAssign.group ? null : itemToAssign}
          preselectedGroup={itemToAssign.group}
          preselectedSoldier={null}
          onAssignmentComplete={handleAssignmentComplete}
        />
      )}

      {/* Edit Item Modal */}
      {editItem && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Edit Equipment</h2>

            <div className="form-group">
              <label>LIN:</label>
              <input
                type="text"
                value={editItem.lin}
                onChange={(e) => setEditItem({...editItem, lin: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label>NSN:</label>
              <input
                type="text"
                value={editItem.nsn}
                readOnly
                className="readonly-input"
              />
            </div>

            <div className="form-group">
              <label>Serial Number:</label>
              <input
                type="text"
                value={editItem.serialNumber || ''}
                onChange={(e) => setEditItem({...editItem, serialNumber: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label>Stock Number:</label>
              <input
                type="text"
                value={editItem.stockNumber || ''}
                onChange={(e) => setEditItem({...editItem, stockNumber: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label>Location:</label>
              <input
                type="text"
                value={editItem.location || ''}
                onChange={(e) => setEditItem({...editItem, location: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label>Maintenance Status:</label>
              <select
                value={editItem.maintenanceStatus || 'OPERATIONAL'}
                onChange={(e) => setEditItem({...editItem, maintenanceStatus: e.target.value})}
              >
                <option value="OPERATIONAL">Operational</option>
                <option value="MAINTENANCE_REQUIRED">Maintenance Required</option>
                <option value="IN_MAINTENANCE">In Maintenance</option>
                <option value="DEADLINED">Deadlined</option>
                <option value="MISSING">Missing</option>
              </select>
            </div>

            <div className="modal-actions">
              <button className="secondary-button" onClick={() => setEditItem(null)}>
                Cancel
              </button>
              <button className="primary-button" onClick={handleSaveEdit}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InventoryList;
